import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import HeaderSidebar from "./Common/HeaderSidebar/HeaderSidebar";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import { useUserDetails } from "@/context";

const DashboardShellContext = createContext({
  hasPersistentSidebar: false,
  registerSidebarProps: () => {},
});

export const useDashboardShell = () => useContext(DashboardShellContext);

const PersistentDashboardShell = ({ children }) => {
  const { isGitchaDefaultWebsite } = useCurrentWebsite();
  const { isUserLoggedIn } = useUserDetails();

  const sidebarPropsRef = useRef({});
  const registerSidebarProps = useCallback((sidebarProps) => {
    sidebarPropsRef.current = sidebarProps || {};
  }, []);

  // NOTE: only the props listed here reach the persistent sidebar. Any new key
  // added to a page's headerSidebarProps must also be bridged here, or it will
  // be silently dropped on BLS routes (the sidebar lives above the router).
  const bridgedSidebarProps = useMemo(
    () => ({
      updateInventoryState: (...args) =>
        sidebarPropsRef.current.updateInventoryState?.(...args),
      onLoginSuccess: (...args) =>
        sidebarPropsRef.current.onLoginSuccess?.(...args),
    }),
    []
  );

  if (isGitchaDefaultWebsite) {
    return (
      <DashboardShellContext.Provider
        value={{ hasPersistentSidebar: false, registerSidebarProps }}
      >
        {children}
      </DashboardShellContext.Provider>
    );
  }

  const showSidebar = isUserLoggedIn;

  return (
    <DashboardShellContext.Provider
      value={{ hasPersistentSidebar: showSidebar, registerSidebarProps }}
    >
      {showSidebar && <HeaderSidebar {...bridgedSidebarProps} />}
      {children}
    </DashboardShellContext.Provider>
  );
};

export default PersistentDashboardShell;
